// eslint-disable-next-line no-restricted-imports
import { type PropsWithChildren, useEffect } from 'react';
import { navigate as navigate_ } from 'gatsby';
import he from 'he';
import moment from 'moment';

export const INTERNAL_LINKS = {
  Notebooks: '/product/notebooks/',
  Magic: '/capability/ai/',
  Explore: '/product/explore/',
  ForTeams: '/capability/hex-for-teams/',
  AppBuilder: '/product/data-apps/',
  Integrations: '/integrations/',
  EmbeddedAnalytics: '/product/embedded-analytics/',
  SemanticModels: '/capability/semantic-models/',
  Customers: '/customers/',
  ClubHex: '/club-hex/',
  ClubHexVIP: '/club-hex-vip/',
  Enterprise: '/enterprise/',
  Resources: '/resources/',
  Templates: '/templates/',
  Careers: '/careers/',
  ContactUs: '/contact-us/',
  RequestADemo: '/request-a-demo/',
  RequestExploreDemo: '/request-explore-demo/',
  Pricing: '/pricing/',
  Partners: '/partners/',
  Blog: '/blog/',
  FurtherReading: '/blog/further-reading/',
  Security: '/security/',
  Newsroom: '/newsroom/',
  DbtCoalesce2024: '/dbt-coalesce-2024/',
  HowToHex: '/how-to-hex/',
  SnowflakeSummit2025: '/resources/hex-at-snowflake-summit-2025/',
  BigDataLondon2024: '/resources/big-data-london-2024/',
  Gartner2025: '/events/gartner-2025/',
  MakeYourDataTeamUnstoppable: '/make-your-data-team-unstoppable/',
  SnowparkContainerServices: '/snowpark-container-services/',
  EmbeddingDemo: '/product/embedding-demo/',
  VsNotebooks: '/vs/notebooks/',
  Watch: '/watch/',
  UseCaseDataScience: '/use-case/data-science/',
  UseCaseExploratoryAnalysis: '/capability/exploratory-analysis/',
  UseCaseOperationalReporting: '/use-case/operational-reporting/',
  UseCaseSelfServe: '/use-case/self-serve/',
  StateOfData: '/state-of-data-teams/',
  StateOfDataEventSeries: '/events/state-of-data-teams-series/',
  SolutionsProductAnalytics: '/solutions/product-analytics/',
  SolutionsDataLeaders: '/solutions/data-leaders/',
  SolutionsMarketing: '/solutions/marketing/',
  SolutionsSales: '/solutions/sales-analytics/',
  SolutionsCustomerSuccess: '/solutions/customer-success/'
};

export function cleanCategories(categories: string) {
  return categories.split(',').map((string: string) => string.trim());
}

export function isEmailValid(email: string) {
  // eslint-disable-next-line no-var
  var re =
    // eslint-disable-next-line no-useless-escape
    /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(email);
}

export function isInternalLink(url: string) {
  return (
    url.includes('https://hex.tech') || ['/', '#'].includes(url.substring(0, 1))
  );
}

export function randomIntFromInterval(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1) + min);
}

export function buildJobPostContent(raw: string) {
  const decodedAndClean = he.decode(raw).replace(/(\r\n|\n|\r)/gm, '');
  const splitUp = decodedAndClean.split('<p>===</p>');

  const frontMatter: Record<string, string> = {};
  let postBody = decodedAndClean;

  if (splitUp.length > 2) {
    const betweenDashes = splitUp[1];
    postBody = splitUp[2];
    const withSep = betweenDashes.replace(/<\/p><p>/g, '</p>|||<p>');
    const properties = withSep.split('|||');
    properties.forEach(function (property) {
      const trimmedUp = property.replace(/<p[^>]*>/g, '').replace(/<\/p>/g, '');
      const tup = trimmedUp.split(': ');
      frontMatter[tup[0]] = tup[1];
    });
  }

  return { frontMatter, postBody };
}

export function cleanURL(slug: string) {
  return encodeURIComponent(
    slug
      .replace('/', '')
      .replace('-', '')
      .replace('–', '')
      .replaceAll(',', '')
      .replaceAll('  ', ' ')
      .replace(/\s+/g, '-')
      .toLowerCase()
  );
}

const ATTRIBUTION_QUERY_PARAMS = ['rhid', 'gclid', 'gad'];
const STORAGE_KEY_ATTRIBUTION = '__ATTRIBUTION_PARAMS__';

function filterAttributionParams(search: string) {
  const searchParams = new URLSearchParams(search);
  for (const [key, value] of searchParams) {
    if (key.includes('utm') || ATTRIBUTION_QUERY_PARAMS.includes(key)) continue;
    searchParams.delete(key, value);
  }
  return searchParams;
}

export function captureAttributionParams() {
  if (typeof window === 'undefined') return;
  const attributionParams = filterAttributionParams(window.location.search);
  window.sessionStorage.setItem(
    STORAGE_KEY_ATTRIBUTION,
    attributionParams.toString()
  );
}

const STORAGE_KEY_USER_PARAMS = '__USER_PARAMS__';

export function storeUserParams(params: { email: string }) {
  window.localStorage.setItem(STORAGE_KEY_USER_PARAMS, JSON.stringify(params));
}

export function captureUserParams() {
  if (typeof window === 'undefined') return;

  const searchParams = new URLSearchParams(window.location.search);
  const email = searchParams.get('email');
  if (!email) return;

  storeUserParams({ email });
}

export function CaptureParams({ children }: PropsWithChildren) {
  useEffect(() => {
    captureAttributionParams();
    captureUserParams();
  }, []);

  return <>{children}</>;
}

export function getUserParams() {
  if (typeof window === 'undefined') return;

  const userParamsRaw = window.localStorage.getItem(STORAGE_KEY_USER_PARAMS);
  if (!userParamsRaw) return;

  return JSON.parse(userParamsRaw);
}

export function getAttributionParams() {
  if (typeof window === 'undefined') return;
  return window.sessionStorage.getItem(STORAGE_KEY_ATTRIBUTION);
}

function combineParams(a: string, b: string) {
  const params1 = new URLSearchParams(a);
  const params2 = new URLSearchParams(b);
  return new URLSearchParams([...params1, ...params2]);
}

export function appendParams(url: string, params: string) {
  const _url = new URL(url);
  _url.search = combineParams(_url.search, params).toString();
  return _url.toString();
}

/**
 * @deprecated persisting url params is bad google analytics practice. If you need
 * to add search params, do so manually or with native `URLSearchParams()` api.
 *
 * Builds a link with only allowed query params
 */
export const buildLinkWithQuery = (to: string, search: string): string => {
  const urlSearchParams = new URLSearchParams(search);
  const filteredParams: Record<string, string> = {};

  urlSearchParams.forEach((val, key) => {
    if (key.includes('utm') || ATTRIBUTION_QUERY_PARAMS.includes(key)) {
      filteredParams[key] = val;
    }
  });

  // if no query params just return base url
  if (Object.keys(filteredParams).length === 0) {
    return to;
  }
  const filteredUrlSearchParams = new URLSearchParams(filteredParams);
  // Check if 'to' already has a query param
  if (to && to.includes('?')) {
    return `${to}&${filteredUrlSearchParams.toString()}`;
  }

  return `${to}?${filteredUrlSearchParams.toString()}`;
};

/**
 * @deprecated just use navigate from gatsby instead
 *
 * This helper function wraps the gatsby `navigate` function, and is designed to
 * ensure that we don't inadvertently drop any query params.
 */
export function navigate(to: string, search: string) {
  return navigate_(buildLinkWithQuery(to, search));
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function createUseCaseLink(useCase: any) {
  return `${INTERNAL_LINKS.Templates}${cleanURL(useCase.category.title)}/${
    useCase.slug
  }/`;
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function createUseCaseCategoryLink(useCase: any) {
  return `${INTERNAL_LINKS.Templates}${useCase.slug}/`;
}
export function capitalizeFirstLetter(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function formatDates(startDate: string, endDate?: null | string) {
  const newStartDate = moment(startDate);

  // if there is no end date...
  // AKA the event is only 1-day long
  if (endDate === null) {
    return newStartDate.format('MMM D, YYYY');
  } else {
    const newEndDate = moment(endDate);

    const startDateMonth = newStartDate.month() + 1;
    const endDateMonth = newEndDate.month() + 1;

    const formattedStartDateMonth = newStartDate.format('MMM');

    // if start and end dates are 2 different months
    // for example: JUNE 26-AUG 2, 2023
    if (startDateMonth !== endDateMonth) {
      const formattedEndDateMonth = newEndDate.format('MMM D, YYYY');

      return `${formattedStartDateMonth} ${newStartDate.date()}-${formattedEndDateMonth} ${newEndDate.date()}, ${newEndDate.year()}`;
    } else {
      // otherwise, if the months are the same
      // for example: MAR 25, 2023
      return `${formattedStartDateMonth} ${newStartDate.date()}-${newEndDate.date()}, ${newEndDate.year()}`;
    }
  }
}

export const TRACKING_LABELS = {
  CopyTemplate: 'Copy template',
  ContactSales: 'Contact sales',
  BlogSubscribe: 'Blog subscribe',
  VideoPlayed: 'Video played',
  TickerItem: 'Ticker item',
  BlogSearch: 'Blog search',
  BlogCommitGraph: 'Blog commit graph',
  BlogFeatured: 'Blog featured',
  BlogRecent: 'Blog recent',
  BlogFeed: 'Blog feed',
  AiProductPagePromptTout: 'Ai product page prompt tout',
  AiProductPagePrimaryCTA: 'Ai product page primary cta',
  AiProductPageSecondaryCTA: 'Ai product page secondary cta',
  AiProductPagePromptClick: 'Ai product page prompt click'
};

export const MODALS = {
  ENUM: 'modal',
  Compute: 'compute',
  Subscribe: 'subscribe'
};

export const INTERVIEW_SLUG = 'friends of data';

export const SIGNUP_LINK = 'https://app.hex.tech/signup';
export const LOGIN_LINK = 'https://app.hex.tech/login';

export const BLOG_SUBSCRIBE_LINK = 'https://hextech.typeform.com/to/Wty1hL7x';

export const MEDIA_KIT_LINK =
  'https://hexhq.notion.site/a091e1bb7bb34019bc4ccf2e3da8645f?v=bb640f9e0ef4499198673561e42a93f0';

export const STATIC_CDN = 'https://static.hex.site';
export const HEX_DEMO_AUTOPLAY = `${STATIC_CDN}/hex-demo-2024-loop.mp4`;
export const HEX_DEMO_VIDEO = `${STATIC_CDN}/hex-demo-2024.mp4`;
export const HEX_DEMO_VIDEO_WHAT_IS_HEX =
  'https://videos.ctfassets.net/mmgv7yhaaeds/6AS6MOl1LactlSomJJApbL/38802ea01afc07373c69274fb43f33ad/what-is-hex.mp4';
export const HOMEPAGE_LOOPING_THUMB_2025 =
  'https://videos.ctfassets.net/mmgv7yhaaeds/2DlD754nzgoTraiKF5Dsa3/c6bd8551ec56ef482a5d34208c300f86/homepage-looping-thumb-2025-compressed.mp4';
export const HEX_EMBEDDED_ANALYTICS_AUTOPLAY =
  'https://videos.ctfassets.net/mmgv7yhaaeds/4quwiBZp1rJeSZEnfwJ9TD/e9c8e5fb1c08658f71b9886674f62427/embeddings_Transition.mp4';

export const TWITTER_LINK = 'https://twitter.com/_hex_tech';
export const LINKEDIN_LINK =
  'https://www.linkedin.com/company/hex-technologies';
export const REQUEST_TEMPLATE_TYPEFORM_LINK =
  'https://hextech.typeform.com/to/ur37J3kZ';
export const SNOWPARK_TYPEFORM_LINK =
  'https://hextech.typeform.com/to/BQyHzqGq';

export const NAV_HEIGHT = 82;

const LEARN_ROOT = 'https://learn.hex.tech';

export const DOCS_LINKS = {
  Home: `${LEARN_ROOT}`,
  Docs: `${LEARN_ROOT}/docs`,
  Changelog: `${LEARN_ROOT}/changelog`,
  Tutorials: `${LEARN_ROOT}/tutorials`,
  HexFoundations: `${LEARN_ROOT}/tutorials/category/hex-foundations`,
  ModernSlaveryStatement: `/files/Hex_UK_Modern_Slavery_Act_Statement.pdf`,
  API: `${LEARN_ROOT}/docs/api/api-overview`,
  AppBuilder: `${LEARN_ROOT}/docs/share-insights/apps/app-builder`,
  AuditLogging: `${LEARN_ROOT}/docs/administration/workspace_settings/audit-logs/audit-logs-overview`,
  Calculations: `${LEARN_ROOT}/docs/explore-data/cells/calculations`,
  Collections: `${LEARN_ROOT}/docs/organize-content/collections`,
  Components: `${LEARN_ROOT}/docs/explore-data/components`,
  ChartCells: `${LEARN_ROOT}/docs/explore-data/cells/visualization-cells/chart-cells`,
  CustomImages: `${LEARN_ROOT}/docs/administration/workspace_settings/custom-images`,
  DataBrowser: `${LEARN_ROOT}/docs/explore-data/data-browser`,
  DataConnections: `${LEARN_ROOT}/docs/connect-to-data/data-connections/data-connections-introduction`,
  DataConnectionPermissions: `${LEARN_ROOT}/docs/connect-to-data/data-connections/data-connections-introduction#workspace-data-connection-permissions`,
  dbt: `${LEARN_ROOT}/docs/connect-to-data/data-connections/dbt-integration`,
  EndorsedStatuses: `${LEARN_ROOT}/docs/organize-content/statuses-categories#endorsed-statuses`,
  EnvironmentViews: `${LEARN_ROOT}/docs/explore-data/projects/environment-configuration/environment-views`,
  Explore: `${LEARN_ROOT}/docs/share-insights/explore`,
  ExploreCalculations: `${LEARN_ROOT}/docs/share-insights/explore#calculations`,
  ExploreCell: `${LEARN_ROOT}/docs/share-insights/explore#explore-cell`,
  ExploreEntryPoints: `${LEARN_ROOT}/docs/share-insights/explore#exploration-entry-points-and-data-sources`,
  ExploreFunctionality: `${LEARN_ROOT}/docs/share-insights/explore#exploration-functionality`,
  ExploreConvertToProject: `${LEARN_ROOT}/docs/share-insights/explore#converting-to-a-project`,
  GitExport: `${LEARN_ROOT}/docs/explore-data/projects/git-export`,
  HistoryAndVersions: `${LEARN_ROOT}/docs/explore-data/projects/history-and-versions`,
  Magic: `${LEARN_ROOT}/docs/hex-magic/what-is-hex-magic`,
  MagicDataPrivacy: `${LEARN_ROOT}/docs/hex-magic/magic-data-privacy`,
  OAuthDataConnections: `${LEARN_ROOT}/docs/connect-to-data/data-connections/oauth-data-connections`,
  Publishing: `${LEARN_ROOT}/docs/share-insights/apps/publish-and-share-apps`,
  PrivacyPolicy: `${LEARN_ROOT}/docs/trust/privacy-policy`,
  QueryCaching: `${LEARN_ROOT}/docs/explore-data/cells/sql-cells/query-caching`,
  QueryMode: `${LEARN_ROOT}/docs/cell-types/sql-cells/overview#query-mode`,
  Quickstart: `${LEARN_ROOT}/tutorials/quickstart`,
  ReactiveExecution: `${LEARN_ROOT}/docs/explore-data/projects/project-execution/execution-model#reactive-graph-based-execution-model`,
  RealTimeCollaboration: `${LEARN_ROOT}/docs/collaborate/real-time-collaboration`,
  Reviews: `${LEARN_ROOT}/docs/collaborate/reviews`,
  Roles: `${LEARN_ROOT}/docs/collaborate/sharing-and-permissions/roles`,
  SaveVersion: `${LEARN_ROOT}/docs/explore-data/projects/history-and-versions#save-a-version`,
  ScheduledRuns: `${LEARN_ROOT}/docs/share-insights/scheduled-runs`,
  Search: `${LEARN_ROOT}/docs/organize-content/workspace-search`,
  SemanticModels: `${LEARN_ROOT}/docs/connect-to-data/semantic-models/intro`,
  SharingPermissions: `${LEARN_ROOT}/docs/collaborate/sharing-and-permissions/sharing-permissions`,
  Snowpark: `${LEARN_ROOT}/docs/connect-to-data/data-connections/snowpark`,
  SQLCells: `${LEARN_ROOT}/docs/explore-data/cells/sql-cells/sql-cells-introduction`,
  TermsAndConditions: `${LEARN_ROOT}/docs/legal/terms-and-conditions`,
  ViewData: `${LEARN_ROOT}/docs/explore-data/cells/visualization-cells/chart-cells#view-data`,
  DataPrivacy: `${LEARN_ROOT}/docs/trust/data-privacy-and-usage-faq`,
  AskMagic: `${LEARN_ROOT}/docs/hex-magic/ask-magic`,
  NotebookAgent: `${LEARN_ROOT}/docs/explore-data/notebook-view/notebook-agent`
};

const TRUST_ROOT = 'https://trust.hex.tech';

export const TRUST_LINKS = {
  Home: `${TRUST_ROOT}`
};

export const DEFAULT_TRANSITION = {
  type: 'tween',
  ease: [0.25, 1, 0.25, 1],
  duration: 0.5
};

export const DEFAULT_OPACITY = 0;

export const STICKY = {
  Boundary: 'sticky-container',
  Element: 'isSticky'
};

export const ANIMATIONS = {
  SimpleFadeIn: {
    initial: {
      opacity: DEFAULT_OPACITY,
      y: 5
    },
    transition: {
      type: 'spring',
      delay: 0.2,
      duration: 0.45,
      bounce: 0
    },
    viewport: { once: true },
    whileInView: { opacity: 1, y: 0 }
  },
  Popover: {
    initial: { opacity: 0, y: 5 },
    transition: {
      type: DEFAULT_TRANSITION.type,
      delay: 0,
      duration: 0.25,
      ease: DEFAULT_TRANSITION.ease
    },
    viewport: { once: true },
    whileInView: { opacity: 1, y: 0 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: 5 }
  },
  Cell: {
    initial: { opacity: 0, y: 25 },
    transition: {
      type: DEFAULT_TRANSITION.type,
      delay: 0.15,
      duration: 0.65,
      ease: DEFAULT_TRANSITION.ease
    },
    viewport: { once: true },
    whileInView: { opacity: 1, y: 0 }
  }
};
